import Vue from 'vue';
import _ from 'lodash';

export default Vue.component('tooltip', {
  template: require('./template.html'),
  data() {
    return {
      active: false,
      darkColor: '#27282b',
      debounce: null,
      tipCoords: {
        left: null,
        right: null,
        top: null,
      },
      arrowCoords: {
        left: null,
        right: null,
        top: null,
      },
      arrowClasses: {
        top: 'editorUi-at',
        bottom: 'editorUi-ab',
        right: 'editorUi-ar',
        left: 'editorUi-al',
      },
    };
  },
  name: 'tooltip',
  props: {
    text: {
      type: String,
      required: true,
      default: '',
    },
    override: {
      type: [String, Boolean],
      required: false,
      default: false,
    },
    tag: {
      type: String,
      required: false,
      default: 'div',
    },
    position: {
      type: String,
      required: false,
      default: 'top',
    },
    classes: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    ellipsis: {
      type: Boolean,
      required: false,
      default: false,
    },
    dark: {
      type: Boolean,
      required: false,
      default: false,
    },
    onClick: {
      type: Function,
      require: false,
      default: () => {},
    },
    html: {
      type: Object,
      required: false,
    },
    wait: {
      type: Number,
      require: false,
      default: 100,
    },
  },
  methods: {
    checkTrigger() {
      if (this.text === '') {
        return;
      }
      if ((this.ellipsis && this.overflown()) || !this.ellipsis) {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(this.moveTip, this.wait);
      }
    },
    moveTip() {
      const $toolTip = this.$refs.tooltip;
      const $el = this.$refs.el;
      const $arrow = this.$refs.arrow;
      const tipContainer = document.getElementById('drzTooltip-container');
      if ($toolTip) {
        tipContainer.appendChild($toolTip);
      }

      const focus = this.ellipsis ? $el.parentNode : $el;
      const windowWidth = window.innerWidth;
      const triggerHeight = ~~($el.offsetHeight);
      const triggerWidth = ~~(focus.offsetWidth);
      const triggerOffsetLeft = this.getOffsets(focus).left;
      const triggerOffsetTop = this.getOffsets($el).top;
      const toolTipWidth = ~~($toolTip.offsetWidth);
      const toolTipHeight = ~~($toolTip.offsetHeight);

      const liveArrowHeight = ~~($arrow.offsetHeight);
      const liveArrowWidth = ~~($arrow.offsetWidth);

      // Reminder: If margins are needed on a tip icon, make sure to add it to the
      // DOM read element and not a psuedo class like :before or :after, because that won't
      // be calculated into the tip position. That is a DOM limitation.

      let leftValue;
      let topValue;

      if (this.position === 'top' || this.position === 'bottom') {
        if (toolTipWidth <= triggerWidth) {
          // if tip is smaller than tip trigger
          leftValue = (triggerOffsetLeft) + ((triggerWidth - toolTipWidth) / 2);
          this.tipCoords.left = `${leftValue}px`;
        } else if (toolTipWidth > triggerWidth) {
          leftValue = (triggerOffsetLeft) - ((toolTipWidth - triggerWidth) / 2);
          // if tip will bleed off to the right or left of window
          if ((leftValue + toolTipWidth) >= (windowWidth)) {
            leftValue = triggerOffsetLeft - (toolTipWidth - triggerWidth);
            this.arrowCoords.left = 'auto';
            this.arrowCoords.right = `${(triggerWidth / 2) - (liveArrowWidth / 2)}px`;
          } else if (leftValue <= 0) {
            leftValue = triggerOffsetLeft;
            this.arrowCoords.right = 'auto';
            this.arrowCoords.left = `${(triggerWidth / 2) - (liveArrowWidth / 2)}px`;
          }
          this.tipCoords.left = `${leftValue}px`;
        }
        if (this.position === 'top') {
          topValue = (triggerOffsetTop - toolTipHeight) - (liveArrowHeight);
          this.tipCoords.top = `${topValue}px`;
        } else if (this.position === 'bottom') {
          topValue = (triggerOffsetTop + triggerHeight) + (liveArrowHeight);
          this.tipCoords.top = `${topValue}px`;
        }
      }
      if (this.position === 'right' || this.position === 'left') {
        if (triggerHeight >= toolTipHeight) {
          topValue = (triggerOffsetTop) + ((triggerHeight - toolTipHeight) / 2);
          this.tipCoords.top = `${topValue}px`;
        } else if (triggerHeight < toolTipHeight) {
          topValue = (triggerOffsetTop) - ((toolTipHeight - triggerHeight) / 2);
          this.tipCoords.top = `${topValue}px`;
        }
        if (this.position === 'right') {
          leftValue = (triggerOffsetLeft + triggerWidth) + liveArrowWidth;
          this.tipCoords.left = `${leftValue}px`;
        } else if (this.position === 'left') {
          leftValue = (triggerOffsetLeft) - (toolTipWidth + liveArrowWidth);
          this.tipCoords.left = `${leftValue}px`;
        }
        // Vertically center arrows for left/right positioned tips
        const arrowTop = (toolTipHeight / 2) - (liveArrowHeight / 2);
        this.arrowCoords.top = `${arrowTop}px`;
      }
      this.active = true;
    },
    resetCoords() {
      this.tipCoords.left = null;
      this.tipCoords.right = null;
      this.tipCoords.top = null;
      this.arrowCoords.left = null;
      this.arrowCoords.right = null;
      this.arrowCoords.top = null;
    },
    removeTip() {
      if (this.text === '') {
        return;
      }
      clearTimeout(this.debounce);
      this.active = false;
      const $toolTip = this.$refs.tooltip;
      const $el = this.$refs.el;
      if ($el) {
        $el.appendChild($toolTip);
      }
      this.resetCoords();
    },
    // Make sure we get the correct offsets if trigger is in a relative parent
    getOffsets(tipIcon) {
      const trigger = tipIcon.getBoundingClientRect();
      const top = trigger.top;
      const left = trigger.left;
      return { top: ~~(top), left: ~~(left) };
    },
    overflown() {
      const $el = this.$refs.el;
      const parent = window.getComputedStyle($el.parentNode, null);
      let parentL = parent.getPropertyValue('padding-left');
      parentL = ~~(parentL.split('px')[0]);
      let parentR = parent.getPropertyValue('padding-right');
      parentR = ~~(parentR.split('px')[0]);
      return ~~($el.offsetWidth) > ~~($el.parentNode.offsetWidth - parentL - parentR);
    },
    wrapperClick() {
      this.removeTip();
      this.onClick();
    },
    startCase(text) {
      return _.startCase(text);
    },
  },
});



// WEBPACK FOOTER //
// ./src/mixins/components/tooltip/index.js